import type { Schemas } from '@npc/api-client'

import { Card, CardTitle } from '../../components/card'
import { StatusDot } from '../../components/tag'
import { duration, hhmm, monthDayTime } from '../../app/format'
import { T } from '../../copy/text'

/**
 * 「最近一次真出事」。
 *
 * ## 那行 meta 里有一句是在结账
 *
 * 「7 月 29 日 03:41 · 全程 4 分钟 12 秒，**你在睡觉**」——最后那半句按
 * `woke_you` 选两条模板中的一条，醒着的时候整句不提它。
 *
 * `alert.incident.meta.asleep` 那条模板的注释把它说得很清楚：
 *
 * > 产品原则第 5 条（沉默是好消息，通知预算极其吝啬）在整个界面上
 * > **只有这一处露面**：它是在跟用户结账——那天夜里出了事，
 * > 而我们**没有把你吵醒**。
 *
 * 所以这半句不是修饰，删了之后「我们替你扛了」这件事就再没地方说了。
 *
 * ## 没有 `duration_seconds` 时整行不画
 *
 * 契约把它标成 nullable，也就是「算不出来」是一种真实状态。
 * 编一个 0 秒出来，读起来是「一瞬间就好了」——那是一句假话。
 */
export function Incident({ incident }: { incident: Schemas['Incident'] }) {
  return (
    <Card>
      <CardTitle className="mb-npc-gap-xs block">
        <T id="alert.incident.heading" />
      </CardTitle>

      {incident.duration_seconds != null ? (
        <div className="mb-npc-card text-npc-caption text-npc-ink-soft">
          <T
            id={incident.woke_you ? 'alert.incident.meta.awake' : 'alert.incident.meta.asleep'}
            vars={{
              when: monthDayTime(incident.started_at),
              duration: duration(incident.duration_seconds),
            }}
          />
        </div>
      ) : null}

      <div className="flex flex-col">
        {incident.entries.map((entry, index) => (
          <Entry
            key={`${entry.at}-${index}`}
            entry={entry}
            last={index === incident.entries.length - 1}
          />
        ))}
      </div>
    </Card>
  )
}

/**
 * 一条。左边时刻、中间一条竖线、线上一个点。
 *
 * 点的颜色走 `entry.tone`——**后端说这条是什么颜色就是什么颜色**，
 * 前端不按文字内容再判一次。那正是两边会走偏的地方。
 */
function Entry({ entry, last }: { entry: Schemas['IncidentEntry']; last: boolean }) {
  return (
    <div className="flex gap-npc-gap-lg">
      <div className="w-[44px] flex-none pt-[2px] font-npc-mono text-npc-meta text-npc-ink-faint">
        {hhmm(entry.at)}
      </div>
      <div
        className={[
          'relative flex-1 border-l border-npc-border pl-npc-card-sm',
          // 最后一条底下不留那截空线——设计稿里线到最后一个点就收住了
          last ? 'pb-0' : 'pb-npc-card',
        ].join(' ')}
      >
        <StatusDot
          tone={entry.tone ?? 'neutral'}
          className="absolute left-[-4.5px] top-[4px] size-[8px]"
        />
        <div className="text-npc-body-sm leading-[1.55]">{entry.text}</div>
      </div>
    </div>
  )
}
